import {
  Checkbox,
  DefaultButton,
  DialogFooter,
  Icon,
  mergeStyleSets,
  TooltipHost,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import GroupPolicyNumberField from "components/controls/GroupPolicyNumberField";
import { useGetFleetInsurerDefaultSettings } from "hooks/data/queries/getFleetInsurerDefaultSettings";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import VehicleTypeDependencyField from "pages/fleet-form/actionDialogs/VehicleTypeDependencyField";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "store/hooks";
import { FleetHullCalculationDefaultSettingType } from "types/types";

const HullPremiumCalculationFormFields = ({
  settingId,
  fieldProps,
  defaultData,
  insurerOptions,
  carUsageOptions,
  vehicleTypeCodeOptions,
  isLoading,
  premiumTypeOptions,
  isTradeLicensePlate,
  setTradeLicensePlate,
  listPriceTypeOptions,
  setIsDirty,
}) => {
  const classes = getClassNames();
  const { setFieldValue, values, errors, handleChange, dirty } = fieldProps;
  const { t } = useTranslation();
  const { fleetId } = useParams() as any;

  const isReadOnly =
    useSelector((s) => s.fleetForm.dialog.type) === EActionDialog.ReadOnly;

  // Info affordance for the optional coverage %s: leaving one empty hides that
  // coverage from the client's add-vehicle form.
  const coverageInfoIcon = (
    <span
      style={{ position: "absolute", right: 0, top: 4, cursor: "pointer", zIndex: 1 }}
    >
      <TooltipHost
        content={t("bfm.hullPremiumCalculation.coverageOptional.info")}
      >
        <Icon iconName="Info" styles={{ root: { fontSize: 16 } }} />
      </TooltipHost>
    </span>
  );

  const valueTypeOptions = [
    {
      value: FleetHullCalculationDefaultSettingType.PREMIUM_RATE_PERCENTAGE,
      label: t("bfm.hullPremiumCalculation.premiumRatePercentage.label"),
    },
    {
      value: FleetHullCalculationDefaultSettingType.ABSOULUTE_PREMIUM_VALUE,
      label: t("bfm.hullPremiumCalculation.absoulutePremiumValue.label"),
    },
  ];

  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);

  // Replace old selectors with new fleet insurer default settings
  const { data: fleetInsurerDefaultSettings } =
    useGetFleetInsurerDefaultSettings(fleetId);

  // Remove old selectors
  // const mtplGroupPolicyInfo = useSelector(
  //   (s) => s.fleetForm.fleetWithPremiums.mtplGroupPolicyInfo
  // );
  // const hullGroupPolicyInfo = useSelector(
  //   (s) => s.fleetForm.fleetWithPremiums.hullGroupPolicyInfo
  // );

  // Create dynamic group policy options based on selected insurer only
  const getInsurerGroupPolicyNumberOptions = useCallback(
    (selectedInsurerValue: number | null) => {
      if (!selectedInsurerValue || !fleetInsurerDefaultSettings) {
        return [];
      }

      const retVal = new Set<any>();

      fleetInsurerDefaultSettings
        .filter(
          (setting) =>
            setting.insurerPartnerNumber === selectedInsurerValue &&
            setting.insurerGroupPolicyNumber !== null &&
            setting.insurerGroupPolicyNumber !== ""
        )
        .map((setting) => ({
          label: setting.insurerGroupPolicyNumber,
          value: setting.insurerGroupPolicyNumber,
        }))
        .forEach((item) => {
          if (!Array.from(retVal).find((it) => it.value === item.value)) {
            retVal.add(item);
          }
        });

      return Array.from(retVal);
    },
    [fleetInsurerDefaultSettings]
  );

  // Replace old useMemo with new dynamic approach - no premium type dependency
  const insurerGroupPolicyNumberOptions = useMemo(() => {
    const selectedInsurerValue = values?.insurerPartnerNumber?.value || null;

    return getInsurerGroupPolicyNumberOptions(selectedInsurerValue);
  }, [values?.insurerPartnerNumber, getInsurerGroupPolicyNumberOptions]);

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty, setIsDirty]);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <Checkbox
            id="isTradeLicensePlate"
            label={t("bfm.tradeLicensePlates.label")}
            styles={{
              root: {
                fontWeight: 600,
              },
            }}
            checked={isTradeLicensePlate}
            disabled={isReadOnly}
            onChange={(ev) => {
              setFieldValue("vehicleType", null);
              setFieldValue("carUsage", null);
              setTradeLicensePlate(!isTradeLicensePlate);
            }}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}></div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <ComboBoxField
            id="premiumType"
            disabled={isReadOnly}
            errors={errors}
            name="premiumType"
            required
            label={"bfm.fleet.premiumType.label"}
            setFieldValue={setFieldValue}
            options={premiumTypeOptions || []}
            defaultValue={values?.premiumType}
            confidentiality={null}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.validFromDate}
            name={"validFromDate"}
            disabled={false}
            label={"bfm.vehicleForm.validFromDate.label"}
            placeholder={t("bfm.selectDate")}
            required
            confidentiality={null}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem}>
          <ComboBoxField
            id="valueType"
            disabled={isReadOnly}
            errors={errors}
            name="valueType"
            required
            label={"bfm.fleet.valueType.label"}
            setFieldValue={setFieldValue}
            options={valueTypeOptions || []}
            defaultValue={
              values?.valueType && typeof values?.valueType === "string"
                ? valueTypeOptions.find((e) => e.value === values?.valueType)
                : values?.valueType
            }
            confidentiality={null}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <DoubleNumberInputField
            maxDecimals={4}
            id="premiumRatePercentage"
            setFieldValue={setFieldValue}
            value={values.premiumRatePercentage}
            onChange={handleChange}
            errors={errors}
            required
            name={"premiumRatePercentage"}
            disabled={isReadOnly}
            label={"bfm.fleet.setting.attributeValue.label"}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <DoubleNumberInputField
            id="absoulutePremiumValue"
            setFieldValue={setFieldValue}
            required
            value={values.premiumFreeSpecialEquipment}
            onChange={handleChange}
            errors={errors}
            name={"premiumFreeSpecialEquipment"}
            disabled={isReadOnly}
            label={
              "bfm.hullPremiumCalculation.premiumFreeSpecialEquipment.label"
            }
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          className={classes.fieldItem}
          style={{ marginRight: "10px", position: "relative" }}
        >
          {coverageInfoIcon}
          <DoubleNumberInputField
            id="gap"
            setFieldValue={setFieldValue}
            value={values.gap}
            onChange={handleChange}
            errors={errors}
            name={"gap"}
            disabled={isReadOnly}
            label={"bfm.hullPremiumCalculation.gap.label"}
          />
        </div>
        <div
          className={classes.fieldItem}
          style={{ marginLeft: "10px", position: "relative" }}
        >
          {coverageInfoIcon}
          <DoubleNumberInputField
            id="replacementValue"
            setFieldValue={setFieldValue}
            value={values.replacementValue}
            onChange={handleChange}
            errors={errors}
            name={"replacementValue"}
            disabled={isReadOnly}
            label={"bfm.hullPremiumCalculation.replacementValue.label"}
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          className={classes.fieldItem}
          style={{ marginRight: "10px", position: "relative" }}
        >
          {coverageInfoIcon}
          <DoubleNumberInputField
            id="grossNegligence"
            setFieldValue={setFieldValue}
            value={values.grossNegligence}
            onChange={handleChange}
            errors={errors}
            name={"grossNegligence"}
            disabled={isReadOnly}
            label={"bfm.hullPremiumCalculation.grossNegligence.label"}
          />
        </div>
        <div
          className={classes.fieldItem}
          style={{ marginLeft: "10px", position: "relative" }}
        >
          {coverageInfoIcon}
          <DoubleNumberInputField
            id="electroPlusPackage"
            setFieldValue={setFieldValue}
            value={values.electroPlusPackage}
            onChange={handleChange}
            errors={errors}
            name={"electroPlusPackage"}
            disabled={isReadOnly}
            label={"bfm.hullPremiumCalculation.electroPlusPackage.label"}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <ComboBoxField
            id="listPriceType"
            disabled={isReadOnly}
            errors={errors}
            name="listPriceType"
            required
            label={"bfm.vehicleForm.listPriceTypeCode.label"}
            setFieldValue={setFieldValue}
            options={listPriceTypeOptions || []}
            defaultValue={values?.listPriceType}
            confidentiality={null}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <ComboBoxField
            id="insurerPartnerNumber"
            disabled={isReadOnly}
            errors={errors}
            required
            name="insurerPartnerNumber"
            label={"bfm.insurer.label"}
            setFieldValue={setFieldValue}
            options={insurerOptions || []}
            defaultValue={values?.insurerPartnerNumber}
            confidentiality={null}
            onChange={(selectedOption) => {
              // When insurer changes, clear the group policy number
              setFieldValue("insurerPartnerNumber", selectedOption);
              setFieldValue("insurerGroupPolicyNumber", null);
            }}
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <DoubleNumberInputField
            id="discountForWaiverOfTaxRefund"
            setFieldValue={setFieldValue}
            value={values.discountForWaiverOfTaxRefund}
            onChange={handleChange}
            required
            errors={errors}
            name={"discountForWaiverOfTaxRefund"}
            disabled={isReadOnly}
            label={
              "bfm.hullPremiumCalculation.discountForWaiverOfTaxRefund.label"
            }
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          {fleetAppSettings?.INCLUDE_GROUP_POLICY ? (
            <GroupPolicyNumberField
              id="insurerGroupPolicyNumber"
              setFieldValue={setFieldValue}
              options={insurerGroupPolicyNumberOptions || []}
              defaultValue={values?.insurerGroupPolicyNumber}
              errors={errors}
              name={"insurerGroupPolicyNumber"}
              disabled={isReadOnly}
              label={"bfm.fleet.setting.insurerGroupPolicyNumber.label"}
              confidentiality={null}
              isClearable={false}
            />
          ) : null}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          className={classes.fieldItem}
          style={{ display: "flex", gap: "15px", marginRight: "10px" }}
        >
          <div style={{ width: "50%" }}>
            <DoubleNumberInputField
              id="listPriceMin"
              setFieldValue={setFieldValue}
              required={
                values.listPriceMin !== "" || values.listPriceMax !== ""
              }
              value={values.listPriceMin}
              onChange={handleChange}
              errors={errors}
              name={"listPriceMin"}
              disabled={isReadOnly}
              label={"bfm.listPriceMin.label"}
            />
          </div>
          <div style={{ width: "50%" }}>
            <DoubleNumberInputField
              id="listPriceMax"
              setFieldValue={setFieldValue}
              required={
                values.listPriceMin !== "" || values.listPriceMax !== ""
              }
              value={values.listPriceMax}
              onChange={handleChange}
              errors={errors}
              name={"listPriceMax"}
              disabled={isReadOnly}
              label={"bfm.listPriceMax.label"}
            />
          </div>
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          {!isTradeLicensePlate ? (
            <ComboBoxField
              id="carUsage"
              disabled={isReadOnly}
              errors={errors}
              name="carUsage"
              label={"bfm.vehicleForm.carUsageCode.label"}
              setFieldValue={setFieldValue}
              options={carUsageOptions || []}
              defaultValue={values?.carUsage}
              confidentiality={null}
              isMulti={true}
            />
          ) : null}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          {!isTradeLicensePlate ? (
            <ComboBoxField
              id="vehicleType"
              disabled={isTradeLicensePlate || isReadOnly}
              errors={errors}
              name="vehicleType"
              required={!isTradeLicensePlate}
              label={"bfm.vehicleForm.vehicleTypeCode.label"}
              setFieldValue={setFieldValue}
              options={vehicleTypeCodeOptions || []}
              defaultValue={values?.vehicleType}
              confidentiality={null}
              isMulti={true}
            />
          ) : null}
        </div>
        <div
          className={classes.fieldItem}
          style={{ marginTop: "0px", marginLeft: "10px" }}
        ></div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <VehicleTypeDependencyField
          values={values}
          vehicleTypeSelected={values?.vehicleType}
          fieldItemStyle={classes.fieldItem}
          errors={errors}
          setFieldValue={setFieldValue}
          selectedDependency={defaultData}
          disabled={isReadOnly}
        />
      </div>
      <div className={classes.placeholder}></div>
      {!isReadOnly && (
        <DialogFooter className={classes.footer}>
          <DefaultButton
            id="saveButton"
            primary
            text={t("greco.save")}
            type="submit"
            iconProps={{
              iconName: "Save",
            }}
            disabled={isLoading}
          />
        </DialogFooter>
      )}
    </>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    placeholder: {
      height: 200,
    },
    fieldItem: {
      width: "100%",
      marginTop: 10,
    },
    footer: {
      marginTop: 50,
    },
  });

export default HullPremiumCalculationFormFields;
